/**
 * ProductImage - 产品图片组件
 *
 * 根据产品 key 和全局配色状态自动显示对应的产品图片
 */

import React, { useEffect } from "react";
import {
  isProductPicMissing,
  productColorInstanceKey,
  readMoudleId,
  registerProductPicLoader as registerSharedProductPicLoader,
  requestProductPic,
  useProductColor,
  type ProductPicLoader,
} from "@eweadn-qiankun/shared-store";

/** 兼容旧调用：转发到 shared-store 的 window 单例（主/子应用共享） */
export function registerProductPicLoader(options: ProductPicLoader) {
  registerSharedProductPicLoader(options);
}

export interface ProductImageProps {
  /** 设备 displayname（鼠标/键盘统一，如 'Z7', 'X87 HE'） */
  displayname: string;

  /**
   * 与首页设备卡片一致：传入当前连接设备的 VID/PID/uuid（来自 equipmentData），
   * 多台同 displayname 时才能命中正确的配色实例。
   */
  colorEquipment?: {
    displayname?: unknown;
    vendorId?: unknown;
    productId?: unknown;
    uuid?: unknown;
    /** 匠盟 mid / 配置表 moudleId；A8 WB Ultra 等同 VID/PID 分色依赖此字段 */
    moudleId?: unknown;
    mid?: unknown;
  };

  /** 图片的 alt 文本 */
  alt?: string;

  /** 自定义类名 */
  className?: string;

  /** 自定义样式 */
  style?: React.CSSProperties;

  /** 图片加载完成回调 */
  onLoad?: () => void;

  /** 图片加载失败回调 */
  onError?: () => void;
}

/**
 * 产品图片组件
 *
 * @example
 * // 基础使用 - 自动获取当前配色和图片
 * <ProductImage displayname="Z7" alt="鼠标" />
 *
 * @example
 * // 带样式和回调
 * <ProductImage
 *   displayname="X87 HE"
 *   alt="键盘"
 *   className="my-product-image"
 *   onLoad={() => console.log('图片加载完成')}
 * />
 */
export const ProductImage: React.FC<ProductImageProps> = ({
  displayname,
  colorEquipment,
  alt = "Product",
  className,
  style,
  onLoad,
  onError,
}) => {
  const { getDeviceImage, canonId } = useProductColor();
  const hasEquipmentHint =
    colorEquipment != null &&
    (colorEquipment.vendorId != null ||
      colorEquipment.productId != null ||
      colorEquipment.uuid != null);
  const imageKey = hasEquipmentHint
    ? productColorInstanceKey({
        displayname,
        vendorId: colorEquipment!.vendorId,
        productId: colorEquipment!.productId,
        uuid: colorEquipment!.uuid,
        moudleId: readMoudleId(colorEquipment),
      })
    : displayname;
  const imageSrc = getDeviceImage(imageKey);
  // 用 imageKey（可含 @@vid:pid:mid:）而不是裸 displayname，
  // 否则 A8 WB Ultra 等 moudleId 分型号会请求成无后缀的空配置。
  const modelKey = canonId(imageKey) || canonId(displayname);

  useEffect(() => {
    if (!modelKey) return;
    requestProductPic(modelKey);
  }, [modelKey]);

  if (isProductPicMissing(imageSrc)) {
    return (
      <span className={className} style={style}>
        暂未上传产品图
      </span>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      style={style}
      loading="lazy"
      decoding="async"
      onLoad={onLoad}
      onError={onError}
    />
  );
};

export default ProductImage;
